import React, { useMemo, useState } from 'react';
import {
  AlertTriangle, CheckCircle, ChevronDown, ChevronRight, Info, AlignLeft,
} from 'lucide-react';
import {
  analyzeHeadings,
  HEADING_LEVEL_BORDER,
  HEADING_LEVEL_TAG,
} from '../utils/headingsAnalysis';

const ROW = 'px-4 py-2.5';
const LABEL = 'text-sm font-medium text-blue-gray-800';
const DETAIL = 'text-xs text-blue-gray-600 leading-relaxed';
const COUNT_PILL = 'text-[10px] font-medium px-1.5 py-0.5 rounded-md border tabular-nums';

export default function HeadingsArchitecturePanel({ headingsList = [], headingsCounts = {} }) {
  const [open, setOpen] = useState(false);

  const analysis = useMemo(
    () => analyzeHeadings(headingsList, headingsCounts),
    [headingsList, headingsCounts],
  );
  const { issues, strengths, hasIssues, hasWarnings } = analysis;
  const total = headingsList.length;
  const countEntries = Object.entries(headingsCounts).filter(([, count]) => count > 0);

  if (total === 0 && countEntries.length === 0) {
    return (
      <p className={`${DETAIL} italic ${ROW}`}>
        Aucune structure de titres détectée. Relancez l&apos;audit SEO de la page pour analyser H1–H6.
      </p>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-2.5 ${ROW} text-left hover:bg-neutral-50/80 transition-colors`}
        aria-expanded={open}
      >
        <AlignLeft size={14} className="text-blue-gray-500 shrink-0" />
        <span className={`${LABEL} flex-1`}>Architecture des titres</span>
        <span className="text-xs text-blue-gray-500 tabular-nums">{total} titre{total > 1 ? 's' : ''}</span>
        <ChevronDown
          size={14}
          className={`text-blue-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="px-4 pb-3 pt-0 space-y-2.5 border-t border-neutral-50">
          <div className="flex flex-wrap gap-1.5 pt-2">
            {countEntries.map(([level, count]) => {
              const lvl = parseInt(level.replace('h', ''), 10);
              return (
                <span
                  key={level}
                  className={`${COUNT_PILL} font-mono ${HEADING_LEVEL_TAG[lvl] || 'bg-blue-gray-100 text-blue-gray-700'}`}
                >
                  {level.toUpperCase()}
                  :
                  {' '}
                  <strong>{count}</strong>
                </span>
              );
            })}
            <span className={`${COUNT_PILL} bg-blue-gray-50 text-blue-gray-600 border-blue-gray-200`}>
              Total:
              {' '}
              <strong>{total}</strong>
            </span>
          </div>

          {!hasIssues && !hasWarnings && (
            <div className="flex items-center gap-2 px-2.5 py-2 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle size={14} className="text-green-600 shrink-0" />
              <span className="text-xs text-green-700 font-medium">Structure des titres correcte</span>
            </div>
          )}

          {issues.length > 0 && (
            <div className="space-y-1.5">
              {issues.map((issue, i) => (
                <div
                  key={i}
                  className={`px-2.5 py-2 rounded-lg border text-xs leading-relaxed ${
                    issue.severity === 'error'
                      ? 'bg-red-50 border-red-200 text-red-800'
                      : 'bg-amber-50 border-amber-200 text-amber-800'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangle
                      size={13}
                      className={`shrink-0 mt-0.5 ${issue.severity === 'error' ? 'text-red-500' : 'text-amber-500'}`}
                    />
                    <div>
                      <p className="font-medium">{issue.message}</p>
                      {issue.fix && (
                        <p className={`${DETAIL} mt-1 flex items-start gap-1`}>
                          <Info size={11} className="shrink-0 mt-0.5" />
                          {issue.fix}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {strengths.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {strengths.slice(0, 4).map((s, i) => (
                <span
                  key={i}
                  className={`${COUNT_PILL} bg-green-50 text-green-700 border-green-200`}
                >
                  {s}
                </span>
              ))}
            </div>
          )}

          {total > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-gray-400">
                Hiérarchie des titres
              </p>
              <div className="rounded-lg border border-neutral-200 overflow-hidden max-h-52 overflow-y-auto">
                {headingsList.map((h, i) => {
                  const indent = (h.level - 1) * 12;
                  const borderColor = HEADING_LEVEL_BORDER[h.level] || 'border-l-blue-gray-300';
                  return (
                    <div
                      key={i}
                      className={`flex items-center gap-1.5 py-1.5 px-2 border-l-[3px] border-b border-b-neutral-50 last:border-b-0 ${borderColor}`}
                      style={{ paddingLeft: `${8 + indent}px` }}
                    >
                      {h.level > 1 && (
                        <ChevronRight size={10} className="text-blue-gray-300 shrink-0" />
                      )}
                      <span
                        className={`text-[10px] font-bold px-1 py-0.5 rounded shrink-0 ${
                          HEADING_LEVEL_TAG[h.level] || 'bg-blue-gray-100 text-blue-gray-600'
                        }`}
                      >
                        {(h.tag || `h${h.level}`).toUpperCase()}
                      </span>
                      <span
                        className={`text-xs truncate ${
                          h.level === 1 ? 'font-medium text-blue-gray-900' : 'text-blue-gray-700'
                        } ${!h.text ? 'italic text-blue-gray-300' : ''}`}
                      >
                        {h.text || '(vide)'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
